import { useState, useEffect, useCallback } from 'react';
import {
  Activity, CheckCircle, XCircle, AlertTriangle, RefreshCw, Clock, Minus,
  ChevronDown, ChevronRight, Play, DollarSign, Database, Zap, Shield,
  GitBranch, Cloud, Wifi, BarChart3, TrendingUp, X
} from 'lucide-react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { colors, Card, Button, LoadingSpinner } from '../../shared';
import { useAuth } from '../../../contexts/AuthContext';
import { useData } from '../../../contexts/DataContext';
import { useOffline } from '../../../contexts/OfflineContext';
import { allTestCategories } from '../../../utils/diagnostics/testDefinitions';
import {
  TEST_STATUS,
  RUN_MODE,
  runSingleTest,
  runCategory,
  runAllTests,
  calculateSummary,
  calculateCategorySummary,
  getTestCounts,
} from '../../../utils/diagnostics/testRunner';
import { formatDuration } from '../../../utils/diagnostics/testHelpers';

// Category icons
const CATEGORY_ICONS = {
  'data-integrity': Database,
  'api-source-validation': Wifi,
  'cross-screen-consistency': BarChart3,
  'function-execution': Zap,
  'permissions': Shield,
  'workflows': GitBranch,
  'ai-operations': DollarSign,
  'storage-offline': Cloud,
};

// Status badge component
const StatusBadge = ({ status, label, size = 'sm' }) => {
  const styles = {
    [TEST_STATUS.PASS]: { bg: colors.greenDim, color: colors.green, icon: CheckCircle },
    [TEST_STATUS.FAIL]: { bg: colors.redDim, color: colors.red, icon: XCircle },
    [TEST_STATUS.WARN]: { bg: colors.orangeDim, color: colors.orange, icon: AlertTriangle },
    [TEST_STATUS.RUNNING]: { bg: colors.yellowDim, color: colors.yellow, icon: RefreshCw },
    [TEST_STATUS.PENDING]: { bg: colors.inputBg, color: colors.textMuted, icon: Clock },
    [TEST_STATUS.SKIP]: { bg: colors.inputBg, color: colors.textMuted, icon: Minus },
  };

  const style = styles[status] || styles[TEST_STATUS.PENDING];
  const Icon = style.icon;
  const iconSize = size === 'sm' ? 12 : 14;
  const textSize = size === 'sm' ? 'text-xs' : 'text-sm';

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg ${textSize} font-medium`}
      style={{ background: style.bg, color: style.color }}
    >
      <Icon size={iconSize} className={status === TEST_STATUS.RUNNING ? 'animate-spin' : ''} />
      {label}
    </span>
  );
};

// Summary bar component
const SummaryBar = ({ summary, isRunning }) => {
  return (
    <div
      className="flex items-center gap-4 p-3 rounded-lg"
      style={{ background: colors.inputBg }}
    >
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium" style={{ color: colors.textPrimary }}>
          {isRunning ? 'Running...' : 'Summary'}
        </span>
      </div>
      <div className="flex items-center gap-3 flex-1">
        <span className="text-sm" style={{ color: colors.green }}>
          {summary.pass} Pass
        </span>
        {summary.fail > 0 && (
          <span className="text-sm" style={{ color: colors.red }}>
            {summary.fail} Fail
          </span>
        )}
        {summary.warn > 0 && (
          <span className="text-sm" style={{ color: colors.orange }}>
            {summary.warn} Warn
          </span>
        )}
        {summary.skip > 0 && (
          <span className="text-sm" style={{ color: colors.textMuted }}>
            {summary.skip} Skip
          </span>
        )}
      </div>
      {summary.totalDuration > 0 && (
        <span className="text-xs" style={{ color: colors.textMuted }}>
          {summary.formattedDuration}
        </span>
      )}
      <StatusBadge
        status={isRunning ? TEST_STATUS.RUNNING : summary.overallStatus}
        label={isRunning ? 'Running' : summary.overallStatus === TEST_STATUS.PASS ? 'All Pass' : summary.overallStatus === TEST_STATUS.WARN ? 'Warnings' : 'Issues'}
      />
    </div>
  );
};

// Test row component
const TestRow = ({ test, result, onRun, context }) => {
  const [expanded, setExpanded] = useState(false);
  const hasDetails = result?.details && result.details.length > 0;
  const isRunning = result?.status === TEST_STATUS.RUNNING;

  return (
    <div
      className="border-b last:border-b-0"
      style={{ borderColor: colors.border }}
    >
      <div className="flex items-center justify-between py-2 px-1">
        <div className="flex items-center gap-2 flex-1">
          {hasDetails ? (
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-0.5 rounded hover:bg-opacity-20"
              style={{ color: colors.textMuted }}
            >
              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          ) : (
            <span className="w-5" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate" style={{ color: colors.textPrimary }}>
              {test.name}
              {test.destructive && (
                <span className="ml-2 text-xs px-1.5 py-0.5 rounded" style={{ background: colors.redDim, color: colors.red }}>
                  Writes
                </span>
              )}
              {test.aiCost && (
                <span className="ml-2 text-xs px-1.5 py-0.5 rounded" style={{ background: colors.yellowDim, color: colors.yellow }}>
                  $
                </span>
              )}
            </p>
            {result?.message && (
              <p className="text-xs truncate" style={{ color: colors.textMuted }}>
                {result.message}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {result?.duration && (
            <span className="text-xs" style={{ color: colors.textMuted }}>
              {formatDuration(result.duration)}
            </span>
          )}
          <StatusBadge
            status={result?.status || TEST_STATUS.PENDING}
            label={
              result?.status === TEST_STATUS.PASS ? 'Pass' :
              result?.status === TEST_STATUS.FAIL ? 'Fail' :
              result?.status === TEST_STATUS.WARN ? 'Warn' :
              result?.status === TEST_STATUS.SKIP ? 'Skip' :
              result?.status === TEST_STATUS.RUNNING ? '...' : '-'
            }
          />
          <button
            onClick={() => onRun(test)}
            disabled={isRunning}
            className="p-1 rounded hover:bg-opacity-20"
            style={{ color: colors.textMuted }}
            title="Run this test"
          >
            <Play size={14} className={isRunning ? 'opacity-30' : ''} />
          </button>
        </div>
      </div>
      {expanded && hasDetails && (
        <div className="pb-2 px-6">
          <div
            className="text-xs p-2 rounded"
            style={{ background: colors.inputBg, color: colors.textSecondary }}
          >
            {result.details.map((detail, i) => (
              <div key={i}>{detail}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Category section component
const CategorySection = ({ category, results, onRunCategory, onRunTest, context, isRunning }) => {
  const [isOpen, setIsOpen] = useState(false);
  const Icon = CATEGORY_ICONS[category.id] || Activity;
  const summary = calculateCategorySummary(results);
  const categoryRunning = Object.values(results || {}).some(r => r.status === TEST_STATUS.RUNNING);

  return (
    <Card className="overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4"
        style={{ borderBottom: isOpen ? `1px solid ${colors.border}` : 'none' }}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ background: colors.yellowDim }}>
            <Icon size={18} style={{ color: colors.yellow }} />
          </div>
          <div className="text-left">
            <span className="font-semibold" style={{ color: colors.textPrimary }}>
              {category.name}
            </span>
            <span className="ml-2 text-sm" style={{ color: colors.textMuted }}>
              ({summary.pass}/{summary.total})
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {category.tests.some(t => t.destructive) && (
            <span
              className="text-xs px-2 py-0.5 rounded"
              style={{ background: colors.redDim, color: colors.red }}
            >
              Writes
            </span>
          )}
          {category.tests.some(t => t.aiCost) && (
            <span
              className="text-xs px-2 py-0.5 rounded"
              style={{ background: colors.yellowDim, color: colors.yellow }}
            >
              $ Cost
            </span>
          )}
          <Button
            size="sm"
            variant="secondary"
            onClick={(e) => {
              e.stopPropagation();
              onRunCategory(category);
            }}
            disabled={isRunning || categoryRunning}
          >
            {categoryRunning ? <RefreshCw size={14} className="animate-spin" /> : <Play size={14} />}
            Run
          </Button>
          {isOpen ? <ChevronDown size={18} style={{ color: colors.textMuted }} />
                  : <ChevronRight size={18} style={{ color: colors.textMuted }} />}
        </div>
      </button>
      {isOpen && (
        <div className="p-3">
          <p className="text-xs mb-3" style={{ color: colors.textMuted }}>
            {category.description}
          </p>
          <div className="space-y-0">
            {category.tests.map(test => (
              <TestRow
                key={test.id}
                test={test}
                result={results?.[test.id]}
                onRun={onRunTest}
                context={context}
              />
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

// ========== API Analytics Components ==========

// Clickable stat card for API metrics
const ApiStatCard = ({ icon: Icon, label, value, subValue, color = colors.yellow, onClick, active }) => (
  <Card
    className={`cursor-pointer transition-all hover:scale-[1.02] ${active ? 'ring-2' : ''}`}
    style={{ ringColor: color }}
    onClick={onClick}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm mb-1" style={{ color: colors.textSecondary }}>{label}</p>
        <p className="text-2xl font-bold" style={{ color: colors.textPrimary }}>{value}</p>
        {subValue && (
          <p className="text-xs mt-1" style={{ color: colors.textMuted }}>{subValue}</p>
        )}
      </div>
      <div className="p-2 rounded-lg" style={{ background: `${color}20` }}>
        <Icon size={20} style={{ color }} />
      </div>
    </div>
    <p className="text-xs mt-2" style={{ color: colors.textMuted }}>Click to view details</p>
  </Card>
);

// Clickable bar for prompt usage
const ClickableBar = ({ label, value, maxValue, color = colors.yellow, errors, avgLatency, onClick, active }) => {
  const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
  return (
    <button
      onClick={onClick}
      className={`w-full text-left mb-3 p-3 rounded-lg transition-all hover:bg-white/5 ${active ? 'ring-2' : ''}`}
      style={{ background: active ? colors.inputBg : 'transparent', ringColor: color }}
    >
      <div className="flex justify-between text-sm mb-1">
        <span style={{ color: colors.textSecondary }}>{label}</span>
        <span style={{ color: colors.textPrimary }}>
          {value.toLocaleString()} calls
          {errors > 0 && <span style={{ color: colors.red }}> ({errors} errors)</span>}
        </span>
      </div>
      <div className="h-2 rounded-full mb-1" style={{ background: colors.inputBg }}>
        <div
          className="h-2 rounded-full transition-all"
          style={{ width: `${percentage}%`, background: color }}
        />
      </div>
      <p className="text-xs" style={{ color: colors.textMuted }}>
        Avg: {Math.round(avgLatency)}ms
      </p>
    </button>
  );
};

// Detail panel for individual log entries
const LogDetailPanel = ({ logs, filter, onClose }) => {
  const [selectedLog, setSelectedLog] = useState(null);
  const [sortBy, setSortBy] = useState('time');

  let filteredLogs = [...logs];
  if (filter.type === 'errors') {
    filteredLogs = filteredLogs.filter(l => !l.success);
  } else if (filter.type === 'prompt') {
    filteredLogs = filteredLogs.filter(l => l.promptId === filter.promptId);
  }

  filteredLogs.sort((a, b) => {
    if (sortBy === 'latency') return (b.latencyMs || 0) - (a.latencyMs || 0);
    if (sortBy === 'status') return (a.success ? 1 : 0) - (b.success ? 1 : 0);
    const aTime = a.calledAt?.toDate?.() || new Date(a.calledAt);
    const bTime = b.calledAt?.toDate?.() || new Date(b.calledAt);
    return bTime - aTime;
  });

  const formatTime = (timestamp) => {
    const date = timestamp?.toDate?.() || new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div
        className="relative ml-auto w-full max-w-2xl h-full flex flex-col overflow-hidden"
        style={{ background: colors.cardBg }}
      >
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: colors.border }}>
          <div>
            <h3 className="font-semibold" style={{ color: colors.textPrimary }}>
              {filter.type === 'all' && 'All API Calls'}
              {filter.type === 'errors' && 'Error Logs'}
              {filter.type === 'prompt' && `Calls: ${filter.promptId}`}
            </h3>
            <p className="text-sm" style={{ color: colors.textMuted }}>
              {filteredLogs.length} entries
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="px-3 py-1 rounded-lg text-sm outline-none"
              style={{ background: colors.inputBg, color: colors.textPrimary }}
            >
              <option value="time">Sort by Time</option>
              <option value="latency">Sort by Latency</option>
              <option value="status">Sort by Status</option>
            </select>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10">
              <X size={20} style={{ color: colors.textMuted }} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filteredLogs.length === 0 ? (
            <div className="p-8 text-center">
              <Activity size={48} className="mx-auto mb-4" style={{ color: colors.textMuted }} />
              <p style={{ color: colors.textMuted }}>No logs match this filter</p>
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: colors.border }}>
              {filteredLogs.map(log => (
                <button
                  key={log.id}
                  onClick={() => setSelectedLog(selectedLog?.id === log.id ? null : log)}
                  className="w-full p-4 text-left hover:bg-white/5 transition-all"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {log.success ? (
                        <CheckCircle size={16} style={{ color: colors.green }} />
                      ) : (
                        <AlertTriangle size={16} style={{ color: colors.red }} />
                      )}
                      <div>
                        <p className="font-medium text-sm" style={{ color: colors.textPrimary }}>
                          {log.promptId}
                        </p>
                        <p className="text-xs" style={{ color: colors.textMuted }}>
                          {formatTime(log.calledAt)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-mono" style={{ color: colors.textPrimary }}>
                        {log.latencyMs || 0}ms
                      </p>
                      <p className="text-xs" style={{ color: colors.textMuted }}>
                        {log.inputTokens || 0} in / {log.outputTokens || 0} out
                      </p>
                    </div>
                  </div>

                  {selectedLog?.id === log.id && (
                    <div
                      className="mt-3 p-3 rounded-lg text-xs space-y-2"
                      style={{ background: colors.inputBg }}
                    >
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span style={{ color: colors.textMuted }}>Product:</span>
                          <span className="ml-2" style={{ color: colors.textPrimary }}>{log.product || 'N/A'}</span>
                        </div>
                        <div>
                          <span style={{ color: colors.textMuted }}>Version:</span>
                          <span className="ml-2" style={{ color: colors.textPrimary }}>v{log.promptVersion || 1}</span>
                        </div>
                        <div>
                          <span style={{ color: colors.textMuted }}>Context ID:</span>
                          <span className="ml-2 font-mono" style={{ color: colors.textPrimary }}>{log.contextId || 'N/A'}</span>
                        </div>
                        <div>
                          <span style={{ color: colors.textMuted }}>User:</span>
                          <span className="ml-2" style={{ color: colors.textPrimary }}>{log.userId || 'N/A'}</span>
                        </div>
                      </div>
                      {!log.success && log.errorMessage && (
                        <div className="p-2 rounded" style={{ background: colors.redDim }}>
                          <p className="font-medium" style={{ color: colors.red }}>Error:</p>
                          <p style={{ color: colors.textPrimary }}>{log.errorMessage}</p>
                        </div>
                      )}
                      <div className="flex justify-between pt-2" style={{ borderTop: `1px solid ${colors.border}` }}>
                        <span style={{ color: colors.textMuted }}>
                          Total tokens: {(log.inputTokens || 0) + (log.outputTokens || 0)}
                        </span>
                        <span style={{ color: colors.textMuted }}>
                          ID: {log.id}
                        </span>
                      </div>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// API Analytics Section Component
const ApiAnalyticsSection = () => {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d');
  const [allLogs, setAllLogs] = useState([]);
  const [stats, setStats] = useState({
    totalCalls: 0,
    successRate: 0,
    avgLatency: 0,
    errorCount: 0,
    promptUsage: [],
    dailyCalls: [],
  });
  const [detailPanel, setDetailPanel] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const now = new Date();
      const daysAgo = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 1;
      const startDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);

      const logsSnapshot = await getDocs(collection(db, 'prompt_usage_logs'));
      const logs = logsSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(log => {
          const logDate = log.calledAt?.toDate?.() || new Date(log.calledAt);
          return logDate >= startDate;
        });

      setAllLogs(logs);

      const totalCalls = logs.length;
      const successfulCalls = logs.filter(l => l.success).length;
      const successRate = totalCalls > 0 ? successfulCalls / totalCalls : 0;
      const avgLatency = totalCalls > 0
        ? logs.reduce((sum, l) => sum + (l.latencyMs || 0), 0) / totalCalls
        : 0;
      const errorCount = logs.filter(l => !l.success).length;

      const promptCounts = {};
      logs.forEach(log => {
        if (!promptCounts[log.promptId]) {
          promptCounts[log.promptId] = { calls: 0, errors: 0, latency: [] };
        }
        promptCounts[log.promptId].calls++;
        if (!log.success) promptCounts[log.promptId].errors++;
        promptCounts[log.promptId].latency.push(log.latencyMs || 0);
      });

      const promptUsage = Object.entries(promptCounts)
        .map(([promptId, data]) => ({
          promptId,
          calls: data.calls,
          errors: data.errors,
          avgLatency: data.latency.length > 0
            ? data.latency.reduce((a, b) => a + b, 0) / data.latency.length
            : 0,
        }))
        .sort((a, b) => b.calls - a.calls);

      const dailyCounts = {};
      logs.forEach(log => {
        const date = (log.calledAt?.toDate?.() || new Date(log.calledAt)).toISOString().split('T')[0];
        dailyCounts[date] = (dailyCounts[date] || 0) + 1;
      });

      const dailyCalls = Object.entries(dailyCounts)
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => a.date.localeCompare(b.date));

      setStats({ totalCalls, successRate, avgLatency, errorCount, promptUsage, dailyCalls });
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const openDetail = (type, promptId = null) => {
    setDetailPanel({ type, promptId });
    setActiveFilter(type === 'prompt' ? promptId : type);
  };

  const closeDetail = () => {
    setDetailPanel(null);
    setActiveFilter(null);
  };

  const maxCalls = Math.max(...stats.promptUsage.map(p => p.calls), 1);

  return (
    <Card className="mb-6">
      <div
        className="flex items-center justify-between p-4 cursor-pointer"
        onClick={() => setIsCollapsed(!isCollapsed)}
        style={{ borderBottom: isCollapsed ? 'none' : `1px solid ${colors.border}` }}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ background: colors.yellowDim }}>
            <BarChart3 size={18} style={{ color: colors.yellow }} />
          </div>
          <div>
            <h3 className="font-semibold" style={{ color: colors.textPrimary }}>
              API Performance Metrics
            </h3>
            <p className="text-sm" style={{ color: colors.textMuted }}>
              Prompt usage and latency data
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={timeRange}
            onChange={(e) => {
              e.stopPropagation();
              setTimeRange(e.target.value);
            }}
            onClick={(e) => e.stopPropagation()}
            className="px-3 py-1 rounded-lg text-sm outline-none"
            style={{ background: colors.inputBg, color: colors.textPrimary }}
          >
            <option value="1d">1d</option>
            <option value="7d">7d</option>
            <option value="30d">30d</option>
          </select>
          <Button
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              loadAnalytics();
            }}
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </Button>
          {isCollapsed ? <ChevronRight size={18} style={{ color: colors.textMuted }} />
                        : <ChevronDown size={18} style={{ color: colors.textMuted }} />}
        </div>
      </div>

      {!isCollapsed && (
        <div className="p-4 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <>
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <ApiStatCard
                  icon={Activity}
                  label="Total API Calls"
                  value={stats.totalCalls.toLocaleString()}
                  color={colors.yellow}
                  onClick={() => openDetail('all')}
                  active={activeFilter === 'all'}
                />
                <ApiStatCard
                  icon={CheckCircle}
                  label="Success Rate"
                  value={`${(stats.successRate * 100).toFixed(1)}%`}
                  subValue={`${stats.totalCalls - stats.errorCount} successful`}
                  color={colors.green}
                  onClick={() => openDetail('all')}
                  active={activeFilter === 'all'}
                />
                <ApiStatCard
                  icon={Clock}
                  label="Avg Latency"
                  value={`${Math.round(stats.avgLatency)}ms`}
                  subValue="Response time"
                  color={colors.orange}
                  onClick={() => openDetail('all')}
                  active={activeFilter === 'all'}
                />
                <ApiStatCard
                  icon={AlertTriangle}
                  label="Error Count"
                  value={stats.errorCount.toLocaleString()}
                  subValue={stats.errorCount > 0 ? 'Click to view errors' : 'No errors'}
                  color={colors.red}
                  onClick={() => stats.errorCount > 0 && openDetail('errors')}
                  active={activeFilter === 'errors'}
                />
              </div>

              {/* Usage by Prompt */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium" style={{ color: colors.textPrimary }}>
                    Usage by Prompt
                  </h4>
                  <p className="text-xs" style={{ color: colors.textMuted }}>Click a prompt to view its calls</p>
                </div>
                {stats.promptUsage.length === 0 ? (
                  <p className="text-sm" style={{ color: colors.textMuted }}>No usage data available</p>
                ) : (
                  <div>
                    {stats.promptUsage.slice(0, 10).map(prompt => (
                      <ClickableBar
                        key={prompt.promptId}
                        label={prompt.promptId}
                        value={prompt.calls}
                        maxValue={maxCalls}
                        color={prompt.errors > 0 ? colors.orange : colors.yellow}
                        errors={prompt.errors}
                        avgLatency={prompt.avgLatency}
                        onClick={() => openDetail('prompt', prompt.promptId)}
                        active={activeFilter === prompt.promptId}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Daily Trend */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium" style={{ color: colors.textPrimary }}>
                    Daily API Calls
                  </h4>
                  <Button variant="secondary" size="sm" onClick={() => openDetail('all')}>
                    View All Logs
                  </Button>
                </div>
                {stats.dailyCalls.length === 0 ? (
                  <p className="text-sm" style={{ color: colors.textMuted }}>No daily data available</p>
                ) : (
                  <>
                    <div className="flex items-end gap-1 h-24">
                      {stats.dailyCalls.map((day) => {
                        const maxDaily = Math.max(...stats.dailyCalls.map(d => d.count), 1);
                        const height = (day.count / maxDaily) * 100;
                        return (
                          <div
                            key={day.date}
                            className="flex-1 rounded-t transition-all hover:opacity-80 cursor-pointer"
                            style={{
                              height: `${height}%`,
                              background: colors.yellow,
                              minHeight: '4px',
                            }}
                            title={`${day.date}: ${day.count} calls`}
                            onClick={() => openDetail('all')}
                          />
                        );
                      })}
                    </div>
                    <div className="flex justify-between text-xs mt-2" style={{ color: colors.textMuted }}>
                      <span>{stats.dailyCalls[0]?.date || ''}</span>
                      <span>{stats.dailyCalls[stats.dailyCalls.length - 1]?.date || ''}</span>
                    </div>
                  </>
                )}
              </div>

              {/* Quick Summary */}
              <div className="p-3 rounded-lg" style={{ background: colors.inputBg }}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span style={{ color: colors.textMuted }}>Total Tokens:</span>
                    <span className="ml-2 font-mono" style={{ color: colors.textPrimary }}>
                      {allLogs.reduce((sum, l) => sum + (l.inputTokens || 0) + (l.outputTokens || 0), 0).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span style={{ color: colors.textMuted }}>Unique Prompts:</span>
                    <span className="ml-2" style={{ color: colors.textPrimary }}>
                      {stats.promptUsage.length}
                    </span>
                  </div>
                  <div>
                    <span style={{ color: colors.textMuted }}>Slowest Call:</span>
                    <span className="ml-2 font-mono" style={{ color: colors.textPrimary }}>
                      {Math.max(...allLogs.map(l => l.latencyMs || 0), 0).toLocaleString()}ms
                    </span>
                  </div>
                  <div>
                    <span style={{ color: colors.textMuted }}>Unique Users:</span>
                    <span className="ml-2" style={{ color: colors.textPrimary }}>
                      {new Set(allLogs.map(l => l.userId).filter(Boolean)).size}
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Detail Panel */}
          {detailPanel && (
            <LogDetailPanel
              logs={allLogs}
              filter={detailPanel}
              onClose={closeDetail}
            />
          )}
        </div>
      )}
    </Card>
  );
};

// Main diagnostics tab component
export default function DiagnosticsTab() {
  const auth = useAuth();
  const data = useData();
  let offline = {};
  try {
    offline = useOffline();
  } catch {
    // Offline context not available
  }

  const [results, setResults] = useState({});
  const [isRunning, setIsRunning] = useState(false);
  const [runMode, setRunMode] = useState(RUN_MODE.SAFE);

  // Build test context
  const context = {
    auth,
    data,
    offline,
  };

  // Calculate test counts
  const testCounts = getTestCounts(allTestCategories, runMode);
  const summary = calculateSummary(results);

  // Handle result updates
  const handleResultUpdate = useCallback((testId, result) => {
    setResults(prev => {
      // Find which category this test belongs to
      for (const category of allTestCategories) {
        const test = category.tests.find(t => t.id === testId);
        if (test) {
          return {
            ...prev,
            [category.id]: {
              ...(prev[category.id] || {}),
              [testId]: result,
            },
          };
        }
      }
      return prev;
    });
  }, []);

  // Run single test
  const handleRunTest = useCallback(async (test) => {
    setIsRunning(true);
    await runSingleTest(test, context, handleResultUpdate);
    setIsRunning(false);
  }, [context, handleResultUpdate]);

  // Run category
  const handleRunCategory = useCallback(async (category) => {
    setIsRunning(true);
    const options = { runMode };
    const categoryResults = await runCategory(category, context, options, handleResultUpdate);
    setResults(prev => ({
      ...prev,
      [category.id]: categoryResults,
    }));
    setIsRunning(false);
  }, [context, runMode, handleResultUpdate]);

  // Run all tests
  const handleRunAll = useCallback(async (mode) => {
    setIsRunning(true);
    setRunMode(mode);

    // Reset results
    const initialResults = {};
    allTestCategories.forEach(cat => {
      initialResults[cat.id] = {};
      cat.tests.forEach(test => {
        initialResults[cat.id][test.id] = { status: TEST_STATUS.PENDING };
      });
    });
    setResults(initialResults);

    const options = { runMode: mode };
    const allResults = await runAllTests(
      allTestCategories,
      context,
      options,
      handleResultUpdate
    );
    setResults(allResults);
    setIsRunning(false);
  }, [context, handleResultUpdate]);

  // Run safe tests on mount
  useEffect(() => {
    handleRunAll(RUN_MODE.SAFE);
  }, []);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-bold" style={{ color: colors.textPrimary }}>
            System Diagnostics
          </h2>
          <p className="text-sm" style={{ color: colors.textMuted }}>
            Full functional test coverage for trades-app
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs" style={{ color: colors.textMuted }}>
            {testCounts.safe} safe / {testCounts.destructive} write / {testCounts.ai} AI
          </span>
          <Button
            onClick={() => handleRunAll(RUN_MODE.SAFE)}
            disabled={isRunning}
            variant="secondary"
          >
            {isRunning && runMode === RUN_MODE.SAFE ? (
              <RefreshCw size={16} className="animate-spin" />
            ) : (
              <Play size={16} />
            )}
            Run Safe Tests
          </Button>
          <Button
            onClick={() => handleRunAll(RUN_MODE.ALL)}
            disabled={isRunning}
            variant="secondary"
          >
            {isRunning && runMode === RUN_MODE.ALL ? (
              <RefreshCw size={16} className="animate-spin" />
            ) : (
              <Play size={16} />
            )}
            Run All
          </Button>
          <Button
            onClick={() => handleRunAll(RUN_MODE.AI)}
            disabled={isRunning}
            variant="primary"
          >
            {isRunning && runMode === RUN_MODE.AI ? (
              <RefreshCw size={16} className="animate-spin" />
            ) : (
              <>
                <DollarSign size={16} />
                Run AI Tests
              </>
            )}
          </Button>
        </div>
      </div>

      {/* API Performance Metrics Section */}
      <ApiAnalyticsSection />

      {/* Diagnostic Tests Section Divider */}
      <div className="flex items-center gap-4 mt-8 mb-4">
        <div className="flex-1 h-px" style={{ background: colors.border }} />
        <span className="text-sm font-medium" style={{ color: colors.textMuted }}>Diagnostic Tests</span>
        <div className="flex-1 h-px" style={{ background: colors.border }} />
      </div>

      {/* Summary */}
      <SummaryBar summary={summary} isRunning={isRunning} />

      {/* Connection Status */}
      <div className="flex items-center gap-4 text-sm" style={{ color: colors.textMuted }}>
        <div className="flex items-center gap-1">
          <Wifi size={14} style={{ color: offline.isOnline ? colors.green : colors.red }} />
          {offline.isOnline ? 'Online' : 'Offline'}
        </div>
        <div>
          User: {auth.userProfile?.email || 'Not logged in'}
        </div>
        <div>
          Role: {auth.userProfile?.role || 'N/A'}
          {auth.isAdmin?.() && ' (Admin)'}
        </div>
        {data.loading && (
          <div className="flex items-center gap-1">
            <LoadingSpinner size="sm" />
            Loading data...
          </div>
        )}
      </div>

      {/* Test Categories */}
      <div className="space-y-4">
        {allTestCategories.map(category => (
          <CategorySection
            key={category.id}
            category={category}
            results={results[category.id] || {}}
            onRunCategory={handleRunCategory}
            onRunTest={handleRunTest}
            context={context}
            isRunning={isRunning}
          />
        ))}
      </div>

      {/* Debug Info */}
      <Card className="overflow-hidden">
        <button
          onClick={() => {}}
          className="w-full flex items-center justify-between p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ background: colors.inputBg }}>
              <Activity size={18} style={{ color: colors.textMuted }} />
            </div>
            <span className="font-semibold" style={{ color: colors.textPrimary }}>
              Debug Info (Raw)
            </span>
          </div>
        </button>
        <div className="p-4">
          <pre
            className="text-xs p-4 rounded-lg overflow-auto max-h-96"
            style={{ background: colors.inputBg, color: colors.textSecondary }}
          >
            {JSON.stringify({
              auth: {
                user: auth.user ? { uid: auth.user.uid, email: auth.user.email } : null,
                userProfile: auth.userProfile,
                isAuthenticated: auth.isAuthenticated,
                canWrite: typeof auth.canWrite === 'function' ? auth.canWrite() : null,
                isAdmin: typeof auth.isAdmin === 'function' ? auth.isAdmin() : null,
                isDemo: typeof auth.isDemo === 'function' ? auth.isDemo() : null,
              },
              data: {
                loading: data.loading,
                workOrders: data.workOrders?.length || 0,
                users: data.users?.length || 0,
                cards: data.cards?.length || 0,
                receipts: data.receipts?.length || 0,
                lockChanges: data.lockChanges?.length || 0,
                inspections: data.inspections?.length || 0,
                consultSessions: data.consultSessions?.length || 0,
              },
              offline: {
                isOnline: offline.isOnline,
                pendingPhotos: offline.pendingPhotoCount,
                pendingForms: offline.pendingFormCount,
                syncStatus: offline.syncStatus,
              },
              testSummary: summary,
            }, null, 2)}
          </pre>
        </div>
      </Card>
    </div>
  );
}
